import { API_ROUTE } from '@/features/backend/config'
import { authFetch, fetchJSON } from '@/utils/http'
import { FileUpload, ThreadListItem, Tool } from './types'
import { openTabAndFocus } from '@/utils/window'
import { ProcessEventStreamOptions } from '@/utils/stream'

interface ChatRunOpts {
  id: string
  prompt: string
  model?: string
  signal?: AbortSignal
  threadId?: string
  systemMessageBuilder?: string
  files?: File[]
  hiddenContent?: string
  skipSearch?: boolean
  skipRAG?: boolean
  skipDatabase?: boolean
  projectId?: string
  deepResearch?: boolean
}

export const fetchTool = async (id: string) => {
  return await fetchJSON(`${API_ROUTE}/tools/s/${id}`)
}

export const getTool = async (slug: string): Promise<Tool> => {
  return await fetchJSON(`${API_ROUTE}/tools/s/${slug}`)
}

export const fetchDemoTool = async () => {
  return await fetchJSON(`${API_ROUTE}/tools/demo`)
}

export const fetchThread = async (id: string) => {
  const response = await fetchJSON(`${API_ROUTE}/tools/threads/${id}`)
  return response
}

export const fetchThreadByProjectId = async (projectId: string) => {
  return await fetchJSON(`${API_ROUTE}/tools/threads/project/${projectId}`)
}

export const createThread = async (
  toolId: string,
  projectId?: string,
  articleId?: string,
) => {
  return await fetchJSON(`${API_ROUTE}/tools/threads`, {
    method: 'POST',
    body: JSON.stringify({
      toolId: toolId,
      projectId: projectId,
      articleId: articleId,
    }),
  })
}

export const updateThread = async (
  threadId: string,
  { properties }: { properties: { title?: string } },
) => {
  return await fetchJSON(`${API_ROUTE}/tools/threads/${threadId}`, {
    method: 'PATCH',
    body: JSON.stringify({ properties }),
  })
}

export const appendMessagesToThread = async (
  threadId: string,
  messages: Array<{
    role: 'user' | 'assistant'
    content: Array<{
      type: 'text'
      text: string
      isTranscription?: boolean
    }>
  }>,
) => {
  return await fetchJSON(`${API_ROUTE}/tools/threads/${threadId}`, {
    method: 'POST',
    body: JSON.stringify({ messages }),
  })
}

export const revertToMessage = async (
  threadId: string,
  messageIndex: number,
) => {
  return await fetchJSON(`${API_ROUTE}/tools/threads/${threadId}/revert`, {
    method: 'POST',
    body: JSON.stringify({
      messageIndex,
    }),
  })
}

type ListThreadsResponse = {
  threads: ThreadListItem[]
  totalCount: number
  nextPageOffset: number | null
  previousPageOffset: number | null
  limit: number
}

export const listThreads = async (
  limit: number,
  offset: number = 0,
  signal?: AbortSignal,
  query?: string,
): Promise<ListThreadsResponse> => {
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  })

  if (query) {
    queryParams.append('search', query)
  }

  return await fetchJSON(
    `${API_ROUTE}/tools/threads?${queryParams.toString()}`,
    { signal },
  )
}

export const listImageHistory = async (
  offset: number = 0,
  signal?: AbortSignal,
): Promise<ListThreadsResponse> => {
  const queryParams = new URLSearchParams({
    limit: '8',
    offset: offset.toString(),
  })

  return await fetchJSON(
    `${API_ROUTE}/tools/image-history?${queryParams.toString()}`,
    { signal },
  )
}

export const softDeleteThread = async (id: string) => {
  return await authFetch(`${API_ROUTE}/tools/threads/${id}`, {
    method: 'DELETE',
  })
}

export const updateUserData = async (uid: string, data: any) => {
  return await fetchJSON(`${API_ROUTE}/users/${uid}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export const updateFavoriteTools = async (tools: string[]) => {
  return await fetchJSON(`${API_ROUTE}/users/favorite-tools`, {
    method: 'PATCH',
    body: JSON.stringify({ favoriteTools: tools }),
  })
}

export const getChatStreamOptions = ({
  id,
  prompt,
  model,
  signal,
  threadId,
  systemMessageBuilder,
  files,
  hiddenContent,
  skipSearch = false,
  skipRAG = false,
  skipDatabase = false,
  projectId,
  deepResearch = false,
}: ChatRunOpts): ProcessEventStreamOptions => {
  const formData = new FormData()
  formData.append('prompt', prompt)
  formData.append('model', model || '')
  formData.append('threadId', threadId || '')
  formData.append('systemMessageBuilder', systemMessageBuilder || '')
  formData.append('hiddenContent', hiddenContent || '')
  formData.append('skipSearch', skipSearch.toString())
  formData.append('skipRAG', skipRAG.toString())
  formData.append('skipDatabase', skipDatabase.toString())
  formData.append('projectId', projectId || '')
  formData.append('deepResearch', deepResearch.toString())

  if (files && files.length > 0) {
    for (const file of files) {
      formData.append('files', file)
    }
  }

  return {
    url: `${API_ROUTE}/tools/${id}/inference/chat`,
    method: 'POST',
    headers: {
      // credentials: 'include' is handled by fetchEventSource internally
    },
    body: formData,
    signal,
  }
}

interface ToolRunOpts {
  tool: Tool
  inputs: any
  signal?: AbortSignal
  threadId?: string
  stream?: boolean
  isDemo?: boolean
}

export const getToolStreamOptions = ({
  tool,
  inputs,
  signal,
  threadId,
  // stream = true,
  isDemo = false,
}: ToolRunOpts): ProcessEventStreamOptions => {
  const endpoint = isDemo ? 'demo' : 'run'

  const fileUploadFields = Array.isArray(tool.toolFields)
    ? (tool.toolFields.filter(
        (f) => f.blockType === 'FileUpload',
      ) as FileUpload[])
    : []

  let body: FormData | string
  let headers: Record<string, string> | undefined

  if (fileUploadFields.length > 0) {
    body = new FormData()

    body.append('inputs', JSON.stringify(inputs))
    body.append('threadId', threadId || '')

    for (const field of fileUploadFields) {
      const uploadedDocs = inputs[field.name]

      if (!uploadedDocs || uploadedDocs.length === 0) {
        continue
      }

      if (!field.allowMultiple) {
        body.append(field.name, uploadedDocs[0], uploadedDocs[0].name)
        continue
      }

      for (const file of uploadedDocs) {
        if (file instanceof File) {
          body.append(field.name, file, file.name)
        }
      }
    }
    headers = undefined // NOTE: The Content-Type is set automatically by the browser
  } else {
    body = JSON.stringify({ inputs, threadId })
    headers = { 'Content-Type': 'application/json' }
  }

  return {
    url: `${API_ROUTE}/tools/${tool.id}/inference/${endpoint}`,
    method: 'POST',
    headers,
    body,
    signal,
  }
}

export const exportToGdocs = async (threadId: string, messageIdx: number) => {
  const response = await fetchJSON(
    `${API_ROUTE}/tools/threads/${threadId}/message/${messageIdx}/export-gdocs`,
  )

  return response.documentUrl as string
}

export const exportDocumentToGdocs = async (
  content: string,
  metadata: {
    title?: string
    name: string
    type?: string
    description?: string
    author?: string
  },
) => {
  const response = await fetchJSON(
    `${API_ROUTE}/tools/documents/export-gdocs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, metadata }),
    },
  )

  return response.documentUrl as string
}

export const exportThreadMessage = async (
  threadId: string,
  messageIdx: number,
  format: 'pdf' | 'docx',
) => {
  const exportURL = `${API_ROUTE}/tools/threads/${threadId}/message/${messageIdx}/export?format=${format}`

  openTabAndFocus(exportURL)
  return
}

export const exportDocument = async (
  content: string,
  metadata: {
    title?: string
    name: string
    type?: string
    description?: string
    author?: string
  },
  format: 'pdf' | 'docx' | 'plain',
) => {
  const exportURL = `${API_ROUTE}/tools/documents/export?format=${format}`

  try {
    const response = await fetch(exportURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ content, metadata }),
    })

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`)
    }

    // Get the file blob
    const blob = await response.blob()

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename: string

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      } else {
        // Fallback: remove current extension and add format extension
        const nameWithoutExt = metadata.name.replace(/\.[^/.]+$/, '')
        const fileExtension = format === 'plain' ? 'md' : format
        filename = `${nameWithoutExt}.${fileExtension}`
      }
    } else {
      // Fallback: remove current extension and add format extension
      const nameWithoutExt = metadata.name.replace(/\.[^/.]+$/, '')
      const fileExtension = format === 'plain' ? 'md' : format
      filename = `${nameWithoutExt}.${fileExtension}`
    }

    // Create temporary URL and download
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting document:', error)
    throw error
  }
}

export const addFeedbackToMessage = async (
  threadId: string,
  messageIdx: number,
  feedback: { liked?: boolean },
) => {
  const body =
    feedback.liked === undefined ? undefined : JSON.stringify(feedback)

  return await fetchJSON(
    `${API_ROUTE}/tools/threads/${threadId}/message/${messageIdx}/feedback`,
    {
      method: 'POST',
      body,
    },
  )
}

export const postToolUseResult = async (
  threadId: string,
  signalId: string,
  result: any,
) => {
  return await fetchJSON(`${API_ROUTE}/signals/complete`, {
    method: 'POST',
    body: JSON.stringify({
      scopeId: threadId,
      signalId: signalId,
      data: {
        signalId,
        signalType: 'toolUse',
        ...result,
      },
    }),
  })
}
